<template>
    <div
        v-show="selectedView == 'table'"
        class="teamschedule-search-grid"
    >
        <!-- Shimmer skeleton: shown during any loading (initial + filter changes) until first page data arrives -->
        <div
            v-if="isLoading && !hasFirstPageData"
            class="grid-shimmer"
        >
            <!-- Fake header row -->
            <div class="grid-shimmer__header">
                <div class="grid-shimmer__header-cell grid-shimmer__header-cell--user">
                    <span class="grid-shimmer__header-bone" />
                </div>
                <div
                    v-for="col in SHIMMER_DATE_COL_COUNT"
                    :key="'sh' + col"
                    class="grid-shimmer__header-cell"
                >
                    <span class="grid-shimmer__header-bone" />
                </div>
            </div>
            <!-- Fake filter row -->
            <div class="grid-shimmer__filters">
                <div class="grid-shimmer__filter-cell grid-shimmer__filter-cell--user">
                    <div class="grid-shimmer__filter-input">
                        <span class="grid-shimmer__filter-placeholder" />
                    </div>
                </div>
                <div
                    v-for="col in SHIMMER_DATE_COL_COUNT"
                    :key="'sf' + col"
                    class="grid-shimmer__filter-cell"
                >
                    <div class="grid-shimmer__filter-input">
                        <span class="grid-shimmer__filter-placeholder" />
                    </div>
                </div>
            </div>
            <!-- Shimmer body rows -->
            <div class="grid-shimmer__body">
                <div
                    v-for="row in 10"
                    :key="'sr' + row"
                    class="grid-shimmer__row"
                >
                    <div class="grid-shimmer__cell grid-shimmer__cell--user">
                        <div class="grid-shimmer__bone" />
                    </div>
                    <div
                        v-for="col in SHIMMER_DATE_COL_COUNT"
                        :key="'sc' + col"
                        class="grid-shimmer__cell"
                    >
                        <div class="grid-shimmer__bone" />
                    </div>
                </div>
            </div>
            <!-- Fake pagination -->
            <div class="grid-shimmer__pagination">
                <span class="grid-shimmer__pagination-bone" />
            </div>
        </div>

        <!-- Real AG Grid: hidden while shimmer is active, always stays mounted for gridReady -->
        <div
            v-show="!isLoading || hasFirstPageData"
            class="teamschedule-search-data-grid"
        >
            <div class="ag-grid-wrapper">
                <AgGridVue
                    class="ag-theme-playvox"
                    :grid-options="gridOptions"
                    :column-defs="columnDefinitions"
                    :row-model-type="'clientSide'"
                    :row-data="rowData"
                    :on-grid-ready="onGridReady"
                    :modules="modules"
                    :pagination="true"
                    :pagination-page-size="pageSizeOption"
                    :cache-block-size="pageSizeOption"
                    :icons="icons"
                />
            </div>
            <div
                v-if="legendItems.length > 0"
                class="roster-legend"
            >
                <div class="legend-title">
                    <span v-if="currentColorMode === 'TEMPLATES'">Shift Templates:</span>
                    <span v-else-if="currentColorMode === 'LOCATIONS'">Locations:</span>
                    <span v-else>Legend:</span>
                </div>
                <div class="legend-items">
                    <div
                        v-for="item in legendItems"
                        :key="item.id"
                        class="legend-item"
                    >
                        <span
                            class="legend-color-box"
                            :style="{ backgroundColor: item.color }"
                        />
                        <span class="legend-label">{{ item.name }}</span>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <div
        v-show="selectedView == 'timeline'"
        class="teamschedule-search-grid"
    >
        <div class="teamschedule-search-data-grid">
            <TeamAndScheduleTimeline
                class="ag-theme-playvox"
                :row-data="(combinedData as unknown as UserData[])"
                :total-teamschedule="totalTeamSchedule"
                :column-display="columnDisplay"
                :group-by="groupBy"
                :is-loading="isLoading"
                :timezone="userTimezone"
                :start-date="body.startTime"
                :end-date="body.endTime"
                :has-view-leave-type-permission="currentUser?.permissions?.includes('can_view_leave_type')"
            />
        </div>
    </div>
</template>

<!-- eslint-disable max-lines -->
<script setup lang="ts">
import {
    ref, inject, onMounted, onBeforeUnmount, computed, watch, nextTick, type Ref
} from 'vue';
import { AgGridVue } from '@ag-grid-community/vue3';
import { GridApi, GridReadyEvent } from '@ag-grid-community/core';
import '@playvox/almendra/dist/components/icon';
import TeamAndScheduleTimeline from './TeamAndScheduleTimeline.vue';
import { AgGridColDef } from '@/types';
import { useModuleSetup } from '@/components/TeamAndSchedule/services/useModuleSetup';
import { getTeamAndScheduleResponse } from '@/components/TeamAndSchedule/services/teamScheduleApi';
import { tz } from 'moment-timezone';
import { useSessionStorage } from '@agyletime/vue-3-common/src/composables/sessionStorage';
import type { InjectedPagePreferences } from '@agyletime/vue-3-common/src/typings/pagePreference';
import { calculateCurrentWeekDates } from '@/components/TeamAndSchedule/utils/dateUtils';
import { useWeekStartDay } from '@/components/TeamAndSchedule/composables/useWeekStartDay';
import { useShiftTooltip } from '@/components/TeamAndSchedule/composables/useShiftTooltip';
import { useCurrentUser } from '@/components/TeamAndSchedule/composables/useCurrentUser';
import { useLogger } from '@agyletime/vue-3-common/src/composables/logger';
import type { UserDateRow, UserData } from '@/components/TeamAndSchedule/utils/types';
import {
    addPageSizeSelector, transformRosterToUserDateGrid, generateUserDateColumnDefs, rebuildLegend, type LegendItem
} from '@/components/TeamAndSchedule/utils/agGridUtils';
import { DEFAULT_PAGE_SIZE } from '@/components/TeamAndSchedule/utils/constants';
import { ModuleRegistry } from '@ag-grid-community/core';
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import { CsvExportModule } from '@ag-grid-community/csv-export';
import {
    generateBodyForTimeSchedule,
    addUserNameInResponse,
    updateUserColumnHeader,
    createGridOptions,
    mergeBackendResponse,
    filterShiftsByUserEndAt,
    type TimeScheduleBody,
    type ShiftResponseItem,
    type UsersDataMap,
    type ApiResponse
} from './teamScheduleHelpers';
import { useTeamAndScheduleWatchers } from './useTeamAndScheduleWatchers';

ModuleRegistry.registerModules([ ClientSideRowModelModule, CsvExportModule ]);

const logger = useLogger('TeamScheduleDataGrid');

interface Props {
    query: Record<string, unknown>;
    activeTable: string;
    selectedView: string;
    groupBy: string;
    colorMode: string;
    columnDisplay: { userId: boolean; email: boolean };
}
const icons = ref({
    filter: '<i class="playvox-icons icon-filter"></i>',
    filterActive: '<i class="playvox-icons icon-filter"></i>',
    sortAscending: '<i class="playvox-icons icon-up"></i>',
    sortDescending: '<i class="playvox-icons icon-down"></i>',
    sortUnSort: ''
});
// Number of date columns to show in the shimmer skeleton during loading (typically 7 for a week view).
const SHIMMER_DATE_COL_COUNT = 7;
const tokens = ref<string[]>([]);
const gridApi = ref<GridApi<unknown> | null>(null);
const totalTeamSchedule = ref(0);
let url = '';
let filterChangedTimerId: ReturnType<typeof setTimeout> | null = null;

const sessionStorage = useSessionStorage();
const modules = useModuleSetup();
const { weekStartDay, fetchWeekStartDay } = useWeekStartDay();
useShiftTooltip();
const queryFilter = ref<Record<string, unknown>>({});
const pageSizeOption = ref(DEFAULT_PAGE_SIZE);
const pageSizeOptions = [
    5, 10, 20
];
const isLoading = ref(true);
const isInitialLoad = ref(true);
const hasFirstPageData = ref(false);
const loadingMessage = ref('Loading data...');
const showProgress = ref(false);
const loadedCount = ref(0);
const totalCount = ref(0);
const progressPercent = computed(() => {
    if (totalCount.value === 0) return 0;
    return Math.round((loadedCount.value / totalCount.value) * 100);
});
const legendItems = ref<LegendItem[]>([]);
const currentColorMode = ref<string>('NONE');
let columnDefinitions = ref<AgGridColDef[]>([]);
const rowData = ref<UserDateRow[]>([]);
const combinedData = ref<ShiftResponseItem[]>([]);
let body: TimeScheduleBody = {
    userIds: '',
    startTime: '',
    endTime: '',
    shiftStartTime: '',
    shiftEndTime: '',
    taskIds: '',
    shiftLocationIds: '',
    taskStartTime: '',
    taskEndTime: '',
    shiftTemplateIds: ''
};

const gridOptions = ref(createGridOptions(onFilterChanged));
const { getCurrentUser } = useCurrentUser();
const currentUser = getCurrentUser();

const resetFilters = inject('resetFilters', ref(false));
const exportCSVTeamAndSchedule = inject('exportCSVTeamAndSchedule', ref(false));
const saveTeamScheduleGridSettings = inject('saveTeamScheduleGridSettings', ref(false));
const restoreTeamScheduleGridSettings = inject('restoreTeamScheduleGridSettings', ref(false));
const userTimezone = inject('userTimezone', ref(tz.guess()));
const pagePreferences = inject<InjectedPagePreferences>('pagePreferences');
const userTimeFormatPreference = inject('userTimeFormatPreference', ref({ timeFormat: '24h' as '12h' | '24h' }));

// Inject progress state from parent — DataGrid writes these so the parent's status bar can display them
const parentProgressState = inject('gridProgressState', {
    isLoading: ref(false),
    isInitialLoad: ref(false),
    message: ref(''),
    showProgress: ref(false),
    percent: ref(0)
}) as {
    isLoading: Ref<boolean>;
    isInitialLoad: Ref<boolean>;
    message: Ref<string>;
    showProgress: Ref<boolean>;
    percent: Ref<number>;
};

// Keep parent's status bar in sync — only show pill during initial load, not filter changes
watch([
    isLoading, isInitialLoad, loadingMessage, showProgress, progressPercent
], () => {
    parentProgressState.isLoading.value = isLoading.value && isInitialLoad.value;
    parentProgressState.isInitialLoad.value = isInitialLoad.value;
    parentProgressState.message.value = loadingMessage.value;
    parentProgressState.showProgress.value = showProgress.value;
    parentProgressState.percent.value = progressPercent.value;
}, { immediate: true });

/* Helper Functions */

/**
 * Core grid rendering logic — transforms data and pushes it to AG Grid.
 * Does NOT touch isLoading or call checkForNoData — callers decide when to finalize.
 */
function renderDataToGrid(data: ShiftResponseItem[], count: number): void {
    // Get usersData for leaveTasks and color context
    const usersData = sessionStorage.get('usersData') as UsersDataMap | null;
    // Extract leaveTasks from usersData and convert to format expected by transform function
    const leaveTasks = (usersData?.teamSchedule?.leaveTasks || []).map((task) => ({
        id: String(task.id),
        name: task.label?.replace(' (Leave)', '') || task.name || String(task.id)
    }));

    const { rows, dateColumns } = transformRosterToUserDateGrid(
        { data, totalCount: count } as unknown as Parameters<typeof transformRosterToUserDateGrid>[0],
        {
            timezone: userTimezone.value,
            startDate: body.startTime,
            endDate: body.endTime,
            leaveTasks,
            hasViewLeaveTypePermission: currentUser?.permissions?.includes('can_view_leave_type'),
            timeFormatPreference: userTimeFormatPreference.value
        }
    );

    rowData.value = rows;
    currentColorMode.value = props.colorMode;
    const colorMode = props.colorMode as 'NONE' | 'TEMPLATES' | 'LOCATIONS';
    const colorContext = {
        colorMode,
        shiftTemplates: usersData?.teamSchedule?.shiftTemplates || [],
        locations: usersData?.teamSchedule?.shiftLocations || []
    };
    columnDefinitions.value = generateUserDateColumnDefs(
        dateColumns as unknown as Parameters<typeof generateUserDateColumnDefs>[0],
        props.columnDisplay as Record<string, boolean>,
        props.groupBy
    ) as AgGridColDef[];
    legendItems.value = rebuildLegend(
        rows,
        colorContext as unknown as Parameters<typeof rebuildLegend>[1]
    );
    totalTeamSchedule.value = count;
    emit('update-total', totalTeamSchedule.value);

    gridApi.value?.setGridOption('context', colorContext);
    gridApi.value?.refreshCells({ force: true });
    // Clear any pending timeout from a previous render pass (e.g. first-page render)
    // before scheduling a new one — ensures only the latest render triggers the header update.
    if (filterChangedTimerId) clearTimeout(filterChangedTimerId);
    filterChangedTimerId = setTimeout(() => {
        onFilterChanged();
    }, 500);
}

/**
 * Public updateGridData — called by watchers (groupBy, colorMode, etc.).
 * Renders data, hides overlays, and sets isLoading = false.
 */
function updateGridData(combinedData: ShiftResponseItem[], totalCount: number): void {
    renderDataToGrid(combinedData, totalCount);
    checkForNoData();
    isLoading.value = false;
}

/**
 * Fetch remaining pages (2..N) in parallel and track progress as each page resolves.
 */
async function fetchRemainingPages(totalShifts: number): Promise<ApiResponse[]> {
    const totalPages = Math.ceil(totalShifts / 30);
    const requests: Promise<ApiResponse>[] = [];
    for (let page = 2; page <= totalPages; page++) {
        requests.push(
            getTeamAndScheduleResponse(url, 30, page, body).then((resp: unknown) => {
                const response = resp as ApiResponse;
                loadedCount.value = Math.min(
                    loadedCount.value + (response.data?.length || 0),
                    totalShifts
                );
                loadingMessage.value = `Loaded ${loadedCount.value} of ${totalShifts} shifts`;
                return response;
            })
        );
    }
    return Promise.all(requests);
}

/**
 * Filter, merge, and render shift data into the grid.
 * Returns the processed array so callers can store it.
 */
type FacetsCast = { [key: string]: { value?: string; boundaries?: string } };
function processAndRender(
    rawData: ShiftResponseItem[],
    usersData: UsersDataMap,
    facetsCast: FacetsCast
): ShiftResponseItem[] {
    const filtered = filterShiftsByUserEndAt(rawData, usersData, props.activeTable);
    const processed = addUserNameInResponse(
        mergeBackendResponse(body.userIds, filtered, facetsCast),
        usersData,
        props.activeTable
    );
    renderDataToGrid(processed, processed.length);
    return processed;
}

/**
 * Build the request body and initialize loading state for a new grid fetch.
 * Returns null if usersData is unavailable (caller should bail out).
 */
function prepareLoadRequest(): FacetsCast | null {
    const usersData = sessionStorage.get('usersData') as UsersDataMap | null;
    if (!usersData) return null;

    const query = queryFilter.value as { facets?: Record<string, Record<string, unknown>> };
    const facets = (query?.facets?.[props.activeTable] ?? {}) as unknown;
    const facetsCast = facets as typeof facets & FacetsCast;
    const defaultDates = calculateCurrentWeekDates(weekStartDay.value).split(',');

    body = generateBodyForTimeSchedule({
        facets: facetsCast,
        usersData,
        activeTable: props.activeTable,
        defaultDates: { start: defaultDates[0], end: defaultDates[1] }
    });
    url = '/schedule/v3/shifts/published/teams';

    // Initialize loading state
    isLoading.value = true;
    hasFirstPageData.value = false;
    showProgress.value = false;
    loadedCount.value = 0;
    totalCount.value = 0;
    loadingMessage.value = isInitialLoad.value ? 'Loading data...' : 'Updating data...';

    return facetsCast;
}

/**
 * Update progress indicators after the first API page returns.
 */
function updateProgressAfterFirstPage(totalShifts: number, firstPageShifts: number): void {
    totalCount.value = totalShifts;
    loadedCount.value = firstPageShifts;
    showProgress.value = totalShifts > 30;
    loadingMessage.value = totalShifts > 30
        ? `Loaded ${firstPageShifts} of ${totalShifts} shifts`
        : 'Loading data...';
}

async function loadGridData(): Promise<void> {
    const facetsCast = prepareLoadRequest();
    if (!facetsCast) return;

    const usersData = sessionStorage.get('usersData') as UsersDataMap;

    try {
        const firstResponse = (await getTeamAndScheduleResponse(
            url, 30, 1, body
        )) as ApiResponse;

        const totalShifts = firstResponse.totalCount ?? 0;
        const firstPageData = firstResponse.data ?? [];
        updateProgressAfterFirstPage(totalShifts, firstPageData.length);

        // Render first page immediately for perceived performance
        const processedFirstPage = processAndRender(firstPageData, usersData, facetsCast);
        combinedData.value = processedFirstPage;

        // Only transition from shimmer → grid when there are actual rows to display.
        // If local filtering removed all first-page rows, keep shimmer visible until
        // the full dataset is processed (avoids a blank grid with no feedback).
        if (processedFirstPage.length > 0) {
            hasFirstPageData.value = true;
            gridApi.value?.hideOverlay();
        }
        await nextTick();

        // Fetch remaining pages in parallel and re-render with the full dataset
        if (totalShifts > 30) {
            const remainingResponses = await fetchRemainingPages(totalShifts);
            const allData = [
                ...firstPageData,
                ...remainingResponses.flatMap((r) => r.data ?? [])
            ];
            combinedData.value = processAndRender(allData, usersData, facetsCast);
            hasFirstPageData.value = true;
        }
        
        // Finalize
        loadedCount.value = totalShifts;
        showProgress.value = false;
        isLoading.value = false;
        isInitialLoad.value = false;
        checkForNoData();
        await nextTick();

    } catch (error) {
        logger.error('Error loading grid data:', error);
        isLoading.value = false;
        showProgress.value = false;
        hasFirstPageData.value = false;
        checkForNoData();
    }
}
/* Grid Setup */
const onGridReady = async (evt: GridReadyEvent) => {
    gridApi.value = evt.api;
    gridApi.value?.closeToolPanel();
    addPageSizeSelector(gridApi.value as GridApi, pageSizeOption, pageSizeOptions, 'teamschedule-search-data-grid');
    checkForNoData();
};
/* Existing Utilities */
function checkForNoData() {
    if (!gridApi.value) return;
    if (totalTeamSchedule.value === 0) gridApi.value.showNoRowsOverlay();
    else gridApi.value.hideOverlay();
}
const resetFacetFilters = () => {
    if (!gridApi.value) return;
    gridApi.value.setFilterModel({});
    tokens.value = [];
};
const refreshClientSide = async () => {
    if (gridApi.value) {
        gridApi.value.refreshCells({ force: true });
        gridApi.value.paginationGoToFirstPage();
        await loadGridData();
    }
};

function onFilterChanged(): void {
    updateUserColumnHeader(gridApi.value, props.groupBy);
}

/* Lifecycle Hooks */
onMounted(async () => {
    await fetchWeekStartDay();
});
onBeforeUnmount(() => {
    if (filterChangedTimerId) clearTimeout(filterChangedTimerId);
});

const props = defineProps<Props>();
const emit = defineEmits<{(e: 'update-total', count: number): void }>();
useTeamAndScheduleWatchers({
    resetFilters,
    exportCSVTeamAndSchedule,
    saveTeamScheduleGridSettings,
    restoreTeamScheduleGridSettings,
    userTimezone,
    userTimeFormatPreference,
    pagePreferences,
    gridApi,
    totalTeamSchedule,
    queryFilter,
    isLoading,
    combinedData,
    props,
    onResetFilters: resetFacetFilters,
    onRefreshClientSide: refreshClientSide,
    onUpdateGridData: updateGridData
});

</script>

<style lang="scss" src="./TeamAndScheduleDataGrid.scss"></style>
